import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Backdrop, CircularProgress } from "@mui/material";
import api from "../api/api";
import ToastMessage from "../utils/mui/righttoptoast";
import { eventBus } from "../utils/eventBus";
import "./orders.css";
import { getPracticeId } from "../config/practice";
import { isDateBookable, isSlotFull, isTypeAllowed, slotRuleError } from "../schedule/providerSlotRules";

/* Inline appointment booking on the Schedule tab. The calls are the ones the
   Schedule popup (popups/schedular.jsx) makes: providers + appointment types,
   a provider's open slots for a day, the patient's future appointments (the
   calendar dots and the upcoming list), create_schedule and cancelschedule.

   "Scheduling" is a shortcut that jumps the calendar ahead (Tomorrow, In 2
   weeks, ...); it does not change what gets booked. "Visit type" is the
   appointment type the booking is made with.

   Per-provider booking rules (schedule/providerSlotRules: slot capacity,
   bookable weekdays, telemedicine-only providers/days) apply exactly as in
   the Schedule popup. Only a practice with profile.slotRules (7825) has any;
   everywhere else the helpers give care's one patient per slot. */

const JUMPS = [
  { key: "", label: "Pick a date" },
  { key: "1d", label: "Tomorrow", n: 1, unit: "day" },
  { key: "1w", label: "In 1 week", n: 1, unit: "week" },
  { key: "2w", label: "In 2 weeks", n: 2, unit: "week" },
  { key: "1M", label: "In 1 month", n: 1, unit: "month" },
  { key: "6w", label: "In 6 weeks", n: 6, unit: "week" },
  { key: "2M", label: "In 2 months", n: 2, unit: "month" },
  { key: "3M", label: "In 3 months", n: 3, unit: "month" },
  { key: "6M", label: "In 6 months", n: 6, unit: "month" },
];

// Slots arrive as "08:15"; show "8:15 AM".
const slotLabel = (t) => {
  const d = dayjs(`2000-01-01 ${t}`);
  return d.isValid() ? d.format("h:mm A") : t;
};

const Chevron = ({ dir }) => (
  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <polyline points={dir === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
  </svg>
);

const SchedulePanel = ({ data }) => {
  const practiseid = getPracticeId();
  const rootRef = useRef(null);
  const today = dayjs().startOf("day");
  const [selectedDate, setSelectedDate] = useState(today);
  const [viewMonth, setViewMonth] = useState(today.startOf("month"));
  const [jump, setJump] = useState("");
  const [providersList, setProviderslist] = useState([]);
  const [provider, setProvider] = useState("");
  const [providername, setProvidername] = useState("");
  const [appointmenttypes, setAppointmentTypes] = useState([]);
  const [appointmentType, setAppointmentType] = useState("");
  const [appointmentname, setAppointmentName] = useState(""); // appointmenttypeid
  const [slotsdata, setSlotsData] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createloading, setCreateLoading] = useState(false);
  const [deleteloading, setDeleteloading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const notify = (message, severity) => setToast({ open: true, message, severity });

  const getslots = async (id, dt) => {
    if (!id) {
      setSlotsData([]);
      return;
    }
    setSlotsLoading(true);
    try {
      const response = await api.post(`/v1/${practiseid}/scheduler/daywise-open-slots`, {
        providerid: id,
        startdate: dt,
      });
      setSlotsData(response.status === 200 ? response.data || [] : []);
    } catch (error) {
      console.log(error);
      setSlotsData([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const getProviders = async () => {
    try {
      const response = await api.get(`/v1/${practiseid}/providers`);
      if (response.status !== 200) return;
      const list = response.data || [];
      setProviderslist(list);
      const appresponse = await api.post(`/v1/${practiseid}/appointment/types`, {
        providerid: list.map((p) => p.providerid),
      });
      if (appresponse.status === 200) {
        const types = appresponse.data || [];
        setAppointmentTypes(types);
        if (types.length) {
          // Default to a follow-up when the practice has one, else the first.
          const pick = types.find((t) => /^follow up$/i.test(t.value)) || types[0];
          setAppointmentType(pick.value);
          setAppointmentName(pick.appointmenttypeid);
        }
      }
      if (list.length) {
        setProvider(list[0].providerid);
        setProvidername(list[0].providername);
        await getslots(list[0].providerid, selectedDate.format("YYYY-MM-DD"));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchFuture = async () => {
    if (!data?.patientid) return;
    setLoading(true);
    try {
      const res = await api.get(`/v1/${practiseid}/appointments/${data.patientid}/future`);
      setTableData(res.status === 200 ? res.data || [] : []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProviders();
    fetchFuture();
  }, [data?.patientid]);

  // "+ Schedule" on the Scheduled card brings the panel into view.
  useEffect(() => {
    const show = () => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    eventBus.on("orders:openSchedule", show);
    return () => eventBus.off("orders:openSchedule", show);
  }, []);

  const pickDate = (d) => {
    const day = d.startOf("day");
    if (day.isBefore(today)) return;
    setSelectedDate(day);
    setViewMonth(day.startOf("month"));
    setSelectedSlot("");
    getslots(provider || providersList[0]?.providerid, day.format("YYYY-MM-DD"));
  };

  const onJump = (key) => {
    setJump(key);
    const j = JUMPS.find((x) => x.key === key);
    if (j?.n) pickDate(today.add(j.n, j.unit));
  };

  const providerChange = (id) => {
    const p = providersList.find((item) => String(item.providerid) === String(id));
    setProvider(id);
    setProvidername(p?.providername || "");
    setSelectedSlot("");
    getslots(id, selectedDate.format("YYYY-MM-DD"));
  };

  const appointmentChange = (value) => {
    setAppointmentType(value);
    setAppointmentName(appointmenttypes.find((t) => t.value === value)?.appointmenttypeid);
  };

  const createappointment = async () => {
    if (!appointmentType) return notify("Please Select Appointment Type", "error");
    if (!provider) return notify("Please Select Provider", "error");
    if (!selectedSlot) return notify("Please Select Time Slot", "error");
    const slotRuleMsg = slotRuleError({
      providerid: provider,
      providername,
      appointmentType,
      date: selectedDate,
      slot: selectedSlot,
      slots: slotsdata,
    });
    if (slotRuleMsg) return notify(slotRuleMsg, "error");
    setCreateLoading(true);
    try {
      const response = await api.post(`/v1/${practiseid}/scheduler/patient/${data?.patientid}/create_schedule`, {
        departmentid: data?.departmentid,
        startdate: selectedDate.format("YYYY-MM-DD"),
        starttime: selectedSlot,
        provider: providername,
        providerid: provider,
        appointmenttype: appointmentType,
        appointmenttypeid: appointmentname,
        notes: "",
      });
      if (response.status == 200) {
        notify("schedule created successfully", "success");
        setSelectedSlot("");
        fetchFuture();
        getslots(provider, selectedDate.format("YYYY-MM-DD"));
      } else {
        notify("Failed to create schedule. Please try again.", "info");
      }
    } catch (error) {
      console.log(error);
      notify("Something Went Wrong", "error");
    } finally {
      setCreateLoading(false);
    }
  };

  const deleteAppointment = async (rowid) => {
    setDeleteloading(rowid);
    try {
      const response = await api.delete(`/v1/${practiseid}/scheduler/cancelschedule`, {
        data: { rowid, notes: "test delete" },
      });
      if (response.status == 200) {
        notify("schedule cancelled successfully", "success");
        fetchFuture();
        getslots(provider, selectedDate.format("YYYY-MM-DD"));
      } else {
        notify("Failed to Cancel schedule. Please try again.", "info");
      }
    } catch (error) {
      console.log(error);
      notify("Something Went Wrong", "error");
    } finally {
      setDeleteloading(false);
    }
  };

  // Month grid, Monday first.
  const weeks = useMemo(() => {
    const first = viewMonth.startOf("month");
    const lead = (first.day() + 6) % 7;
    const days = first.daysInMonth();
    const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => first.date(i + 1))];
    while (cells.length % 7) cells.push(null);
    return Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
  }, [viewMonth]);

  const apptDays = useMemo(
    () => new Set(tableData.map((r) => (r.startdate ? dayjs(r.startdate).format("YYYY-MM-DD") : null)).filter(Boolean)),
    [tableData],
  );
  const openCount = slotsdata.filter((s) => !isSlotFull(s, provider, selectedDate)).length;
  // Types this provider can be booked for on the picked day (every type
  // without a rule). A type that stops being allowed is dropped from the
  // selection so the Book button cannot send it.
  const allowedTypes = appointmenttypes.filter((t) => isTypeAllowed(provider, t?.value, selectedDate));
  useEffect(() => {
    if (appointmentType && !allowedTypes.some((t) => t.value === appointmentType)) {
      const pick = allowedTypes[0];
      setAppointmentType(pick ? pick.value : "");
      setAppointmentName(pick ? pick.appointmenttypeid : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, selectedDate, appointmenttypes]);

  return (
    <div className="bmor bmor-sched" ref={rootRef}>
      <div className="bmor-cal">
        <div className="bmor-cal-head">
          <button
            type="button"
            className="bmor-cal-nav"
            aria-label="Previous month"
            disabled={!viewMonth.isAfter(today.startOf("month"))}
            onClick={() => setViewMonth(viewMonth.subtract(1, "month"))}
          >
            <Chevron dir="left" />
          </button>
          <span className="bmor-cal-title">{viewMonth.format("MMMM YYYY")}</span>
          <button
            type="button"
            className="bmor-cal-nav"
            aria-label="Next month"
            onClick={() => setViewMonth(viewMonth.add(1, "month"))}
          >
            <Chevron dir="right" />
          </button>
        </div>
        <div className="bmor-cal-grid" role="grid">
          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
            <span key={i} className="bmor-cal-dow">
              {d}
            </span>
          ))}
          {weeks.flat().map((d, i) =>
            d ? (
              <button
                key={i}
                type="button"
                className={`bmor-cal-day${d.isSame(selectedDate, "day") ? " on" : ""}${
                  d.isBefore(today) ? " past" : ""
                }${apptDays.has(d.format("YYYY-MM-DD")) ? " has" : ""}`}
                disabled={d.isBefore(today) || !isDateBookable(provider, d)}
                aria-pressed={d.isSame(selectedDate, "day")}
                onClick={() => {
                  setJump("");
                  pickDate(d);
                }}
              >
                {d.date()}
              </button>
            ) : (
              <span key={i} />
            ),
          )}
        </div>
        <div className="bmor-cal-legend">
          <span className="bmor-cal-dot" />
          Has an appointment
        </div>
      </div>

      <div className="bmor-sched-main">
        <div className="bmor-sched-fields">
          <label className="bmor-field">
            <span className="bmor-field-label">Scheduling</span>
            <select className="bmos-select" value={jump} onChange={(e) => onJump(e.target.value)}>
              {JUMPS.map((j) => (
                <option key={j.key} value={j.key}>
                  {j.label}
                </option>
              ))}
            </select>
          </label>
          <label className="bmor-field">
            <span className="bmor-field-label">Provider</span>
            <select className="bmos-select" value={provider} onChange={(e) => providerChange(e.target.value)}>
              {providersList.map((p) => (
                <option key={p.providerid} value={p.providerid}>
                  {p.providername}
                </option>
              ))}
            </select>
          </label>
          <label className="bmor-field">
            <span className="bmor-field-label">Visit type</span>
            <select className="bmos-select" value={appointmentType} onChange={(e) => appointmentChange(e.target.value)}>
              <option value="" disabled>
                Select a visit type
              </option>
              {allowedTypes.map((t, i) => (
                <option key={i} value={t.value}>
                  {t.value}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="bmor-sched-day">
          <span className="bmor-sched-date">{selectedDate.format("ddd, MMM D")}</span>
          {slotsdata.length > 0 && (
            <span className="bmor-sched-open">
              {openCount} of {slotsdata.length} times open
            </span>
          )}
        </div>
        {slotsLoading ? (
          <div className="bmor-empty-line">Loading times…</div>
        ) : slotsdata.length === 0 ? (
          <div className="bmor-sched-none">No open slots available for this provider on this day.</div>
        ) : (
          <div className="bmor-slots">
            {slotsdata.map((slot) => {
              const taken = isSlotFull(slot, provider, selectedDate);
              return (
                <button
                  key={slot.starttime}
                  type="button"
                  className={`bmor-slot${selectedSlot === slot.starttime ? " on" : ""}`}
                  disabled={taken}
                  title={taken ? "Full" : undefined}
                  aria-pressed={selectedSlot === slot.starttime}
                  onClick={() => setSelectedSlot(slot.starttime)}
                >
                  {slotLabel(slot.starttime)}
                </button>
              );
            })}
          </div>
        )}

        <button
          type="button"
          className="bmor-book"
          disabled={!selectedSlot || createloading}
          onClick={createappointment}
        >
          {createloading
            ? "Booking…"
            : selectedSlot
              ? `Book ${selectedDate.format("ddd, MMM D")} at ${slotLabel(selectedSlot)}`
              : "Pick a time"}
        </button>

        <div className="bmor-section" style={{ marginTop: 22 }}>
          <span className="bmor-section-title">Upcoming appointments</span>
          {tableData.length > 0 && <span className="bmor-count">{tableData.length}</span>}
        </div>
        {loading ? (
          <div className="bmor-empty-line">Loading…</div>
        ) : tableData.length ? (
          <div className="bmor-upcoming">
            {tableData.map((row) => {
              const d = row.startdate ? dayjs(row.startdate) : null;
              return (
                <div key={row.id ?? row.appointmentid} className="bmor-upcoming-row">
                  <div className="bmor-date">
                    <div className="bmor-date-m">{d ? d.format("MMM").toUpperCase() : "—"}</div>
                    <div className="bmor-date-d">{d ? d.format("D") : "—"}</div>
                  </div>
                  <div className="bmor-item-main">
                    <div className="bmor-item-name">{row.purpose || row.type || "Appointment"}</div>
                    <div className="bmor-item-sub">
                      {[
                        d && d.format("ddd, MMM D"),
                        row.starttime ? dayjs(`2000-01-01 ${row.starttime}`).format("h:mm A") : null,
                        row.providername,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="bmor-tool"
                    disabled={deleteloading === row.id}
                    onClick={() => deleteAppointment(row.id)}
                  >
                    {deleteloading === row.id ? "Cancelling…" : "Cancel"}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bmor-empty-line">No upcoming appointments</div>
        )}
      </div>

      <ToastMessage
        message={toast.message}
        severity={toast.severity}
        open={toast.open}
        handleClose={() => setToast((t) => ({ ...t, open: false }))}
      />
      <Backdrop
        sx={{ color: "secondary.main", backgroundColor: "rgba(255, 255, 255, 0.5)", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={createloading}
      >
        <CircularProgress color="secondary" />
      </Backdrop>
    </div>
  );
};

export default SchedulePanel;
